import { collection, doc, onSnapshot, orderBy, query, type Unsubscribe } from 'firebase/firestore';
import { db } from '@/firebase-config';
import { logger } from '@/utils/logger';

import { buildProjectSubcollectionPath, type ProjectScope } from '../sync/project-scope';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from '../sync/structured-entity-write';
import type { CostumePiece, CostumeSizingProfile, Fitting, MeasurementProfile, QuickChangeTransition } from './types';

const COSTUME_PIECES_COLLECTION = 'costumePieces';
const COSTUME_MEASUREMENTS_COLLECTION = 'costumeMeasurements';
const COSTUME_SIZING_PROFILES_COLLECTION = 'costumeSizingProfiles';
const COSTUME_FITTINGS_COLLECTION = 'costumeFittings';
const COSTUME_QUICK_CHANGES_COLLECTION = 'costumeQuickChanges';

const COSTUMES_WRITE_AUTH_ERROR = 'Authentication required to preserve costumes project writes';

type CostumeWriteOptions = {
    actorId?: string;
};

const COSTUME_PIECE_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'department_tracking',
    capability: 'EDIT_COSTUMES',
    conflictPrefix: COSTUME_PIECES_COLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: COSTUMES_WRITE_AUTH_ERROR,
};

const COSTUME_MEASUREMENT_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'department_tracking',
    capability: 'EDIT_COSTUMES',
    conflictPrefix: COSTUME_MEASUREMENTS_COLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: COSTUMES_WRITE_AUTH_ERROR,
};

const COSTUME_SIZING_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'department_tracking',
    capability: 'EDIT_COSTUMES',
    conflictPrefix: COSTUME_SIZING_PROFILES_COLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: COSTUMES_WRITE_AUTH_ERROR,
};

const OWN_COSTUME_SIZING_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'department_tracking',
    capability: 'UPDATE_OWN_PROFILE',
    conflictPrefix: COSTUME_SIZING_PROFILES_COLLECTION,
    allowedKinds: ['patchEntity'],
    authErrorMessage: COSTUMES_WRITE_AUTH_ERROR,
};

const COSTUME_FITTING_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'department_tracking',
    capability: 'EDIT_COSTUMES',
    conflictPrefix: COSTUME_FITTINGS_COLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: COSTUMES_WRITE_AUTH_ERROR,
};

const COSTUME_QUICK_CHANGE_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'department_tracking',
    capability: 'EDIT_COSTUMES',
    conflictPrefix: COSTUME_QUICK_CHANGES_COLLECTION,
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: COSTUMES_WRITE_AUTH_ERROR,
};

function costumeCollection(scope: ProjectScope, collectionName: string) {
    return collection(db, buildProjectSubcollectionPath(scope, collectionName));
}

function costumeDocument(scope: ProjectScope, collectionName: string, entityId: string) {
    return doc(db, buildProjectSubcollectionPath(scope, collectionName), entityId);
}

export function subscribeToCostumePieces(
    scope: ProjectScope,
    onUpdate: (pieces: CostumePiece[]) => void
): Unsubscribe {
    return onSnapshot(
        costumeCollection(scope, COSTUME_PIECES_COLLECTION),
        (snap) => {
            onUpdate(snap.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id } as CostumePiece)));
        },
        (error) => {
            logger.error('[costumes-api] Costume pieces subscription error:', error);
        }
    );
}

export function subscribeToMeasurementProfiles(
    scope: ProjectScope,
    onUpdate: (measurements: MeasurementProfile[]) => void
): Unsubscribe {
    return onSnapshot(
        costumeCollection(scope, COSTUME_MEASUREMENTS_COLLECTION),
        (snap) => {
            onUpdate(snap.docs.map((docSnap) => ({
                ...docSnap.data(),
                actorId: docSnap.id,
            } as MeasurementProfile)));
        },
        (error) => {
            logger.error('[costumes-api] Costume measurements subscription error:', error);
        }
    );
}

export function subscribeToCostumeSizingProfiles(
    scope: ProjectScope,
    onUpdate: (profiles: CostumeSizingProfile[]) => void
): Unsubscribe {
    return onSnapshot(
        costumeCollection(scope, COSTUME_SIZING_PROFILES_COLLECTION),
        (snap) => {
            onUpdate(snap.docs.map((docSnap) => ({
                ...docSnap.data(),
                actorId: docSnap.id,
            } as CostumeSizingProfile)));
        },
        (error) => {
            logger.error('[costumes-api] Costume sizing profiles subscription error:', error);
        }
    );
}


export function subscribeToOwnCostumeSizingProfile(
    scope: ProjectScope,
    actorId: string,
    onUpdate: (profile: CostumeSizingProfile | null) => void
): Unsubscribe {
    return onSnapshot(
        costumeDocument(scope, COSTUME_SIZING_PROFILES_COLLECTION, actorId),
        (snap) => {
            onUpdate(snap.exists()
                ? { ...snap.data(), actorId: snap.id } as CostumeSizingProfile
                : null);
        },
        (error) => {
            logger.error('[costumes-api] Costume sizing profile subscription error:', error);
        }
    );
}


export function subscribeToFittings(
    scope: ProjectScope,
    onUpdate: (fittings: Fitting[]) => void
): Unsubscribe {
    const fittingsQuery = query(costumeCollection(scope, COSTUME_FITTINGS_COLLECTION), orderBy('date', 'asc'));
    return onSnapshot(
        fittingsQuery,
        (snap) => {
            onUpdate(snap.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id } as Fitting)));
        },
        (error) => {
            logger.error('[costumes-api] Costume fittings subscription error:', error);
        }
    );
}

export function subscribeToQuickChangeTransitions(
    scope: ProjectScope,
    onUpdate: (transitions: QuickChangeTransition[]) => void
): Unsubscribe {
    return onSnapshot(
        costumeCollection(scope, COSTUME_QUICK_CHANGES_COLLECTION),
        (snap) => {
            onUpdate(snap.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id } as QuickChangeTransition)));
        },
        (error) => {
            logger.error('[costumes-api] Costume quick changes subscription error:', error);
        }
    );
}

export async function saveCostumePiece(
    scope: ProjectScope,
    piece: CostumePiece,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_PIECE_WRITE_REGISTRATION,
        entityId: piece.id,
        kind: 'patchEntity',
        payload: piece,
        actorId: options.actorId,
    });
}

export async function deleteCostumePiece(
    scope: ProjectScope,
    pieceId: string,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_PIECE_WRITE_REGISTRATION,
        entityId: pieceId,
        kind: 'deleteEntity',
        payload: { id: pieceId },
        actorId: options.actorId,
    });
}

export async function saveMeasurementProfile(
    scope: ProjectScope,
    measurement: MeasurementProfile,
    options: CostumeWriteOptions = {}
): Promise<void> {
    const { sizing: _sizing, appearance: _appearance, ...measurementPayload } = measurement;
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_MEASUREMENT_WRITE_REGISTRATION,
        entityId: measurement.actorId,
        kind: 'patchEntity',
        payload: measurementPayload,
        actorId: options.actorId,
    });
}

export async function saveCostumeSizingProfile(
    scope: ProjectScope,
    profile: CostumeSizingProfile,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_SIZING_WRITE_REGISTRATION,
        entityId: profile.actorId,
        kind: 'patchEntity',
        payload: profile,
        actorId: options.actorId,
    });
}

export async function deleteCostumeSizingProfile(
    scope: ProjectScope,
    actorId: string,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_SIZING_WRITE_REGISTRATION,
        entityId: actorId,
        kind: 'deleteEntity',
        payload: { id: actorId },
        actorId: options.actorId,
    });
}

export async function saveOwnCostumeSizingProfile(
    scope: ProjectScope,
    profile: CostumeSizingProfile,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: OWN_COSTUME_SIZING_WRITE_REGISTRATION,
        entityId: profile.actorId,
        kind: 'patchEntity',
        payload: profile,
        actorId: options.actorId,
    });
}

export async function deleteMeasurementProfile(
    scope: ProjectScope,
    actorId: string,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_MEASUREMENT_WRITE_REGISTRATION,
        entityId: actorId,
        kind: 'deleteEntity',
        payload: { id: actorId },
        actorId: options.actorId,
    });
}

export async function saveFitting(
    scope: ProjectScope,
    fitting: Fitting,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_FITTING_WRITE_REGISTRATION,
        entityId: fitting.id,
        kind: 'patchEntity',
        payload: fitting,
        actorId: options.actorId,
    });
}

export async function deleteFitting(
    scope: ProjectScope,
    fittingId: string,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_FITTING_WRITE_REGISTRATION,
        entityId: fittingId,
        kind: 'deleteEntity',
        payload: { id: fittingId },
        actorId: options.actorId,
    });
}

export async function saveQuickChangeTransition(
    scope: ProjectScope,
    transition: QuickChangeTransition,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_QUICK_CHANGE_WRITE_REGISTRATION,
        entityId: transition.id,
        kind: 'patchEntity',
        payload: transition,
        actorId: options.actorId,
    });
}

export async function deleteQuickChangeTransition(
    scope: ProjectScope,
    transitionId: string,
    options: CostumeWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: COSTUME_QUICK_CHANGE_WRITE_REGISTRATION,
        entityId: transitionId,
        kind: 'deleteEntity',
        payload: { id: transitionId },
        actorId: options.actorId,
    });
}
